import { ApplePay } from '@adyen/adyen-web'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AdyenCheckoutStatus } from '@/lib/enums'

import type { SessionResult } from '@/lib/types'

interface UseApplePayOptions {
  /** Reads the checkout core created by the Card flow's `mount()`; `null` until then. */
  getCheckout: () => unknown | null
  /** Reads the active Adyen session id, set by the Card flow's `mount()`. */
  getSessionId: () => string | null
  /** Claims the shared session against the internal API. */
  claimSession: (sessionId: string) => Promise<unknown>
  /** Resolves once one of the shared Adyen callbacks settles the session result. */
  waitForSessionResult: () => Promise<SessionResult>
  /** Shared checkout status setter — Apple Pay and Card drive the same status. */
  setStatus: (status: AdyenCheckoutStatus) => void
}

interface UseApplePayReturn {
  /** True once the mounted ApplePay component's `isAvailable()` resolves. */
  applePayAvailable: boolean
  /** See specs/app/index.md::Apple Pay. */
  mountApplePay: (el: HTMLElement) => Promise<void>
  /** See specs/app/index.md::Apple Pay. */
  submitApplePay: () => Promise<SessionResult>
  /** Reads the in-flight claim promise fired by `submitApplePay`, or `null`. See specs/app/index.md::Apple Pay. */
  getPendingClaim: () => Promise<unknown> | null
}

/**
 * Owns the Adyen ApplePay component on top of the checkout core the Card flow
 * (`useAdyenCheckout`) creates. See specs/app/index.md::Apple Pay and Decisions.
 */
export function useApplePay(options: UseApplePayOptions): UseApplePayReturn {
  const applePayComponentRef = useRef<unknown>(null)
  const applePayClaimRef = useRef<Promise<unknown> | null>(null)
  const rejectApplePayAttemptRef = useRef<((error: Error) => void) | null>(null)
  const [applePayAvailable, setApplePayAvailable] = useState(false)

  const mountApplePay = useCallback(
    async (el: HTMLElement) => {
      const checkout = options.getCheckout() as { paymentMethodsResponse: { has: (type: string) => boolean } } | null
      if (!checkout) return
      if (!checkout.paymentMethodsResponse.has('applepay')) return

      const applePayConfig = {
        showPayButton: false,
        renderApplePayCodeAs: 'modal', // see specs/app/index.md::Decisions
        onClick: (resolve: () => void) => resolve(),
        onError: (error: unknown) => {
          const e = error instanceof Error ? error : new Error(String(error))
          if (e.name !== 'CANCEL') {
            Object.assign(e, { state: { isValid: true } })
            console.error('useApplePay.mountApplePay onError', e)
          }
          rejectApplePayAttemptRef.current?.(e)
        },
      }
      // biome-ignore lint/suspicious/noExplicitAny: Adyen SDK 6 types are loose
      const applePay = new ApplePay(checkout as any, applePayConfig as any)

      try {
        await applePay.isAvailable()
      } catch {
        setApplePayAvailable(false)
        return
      }

      applePay.mount(el)
      applePayComponentRef.current = applePay
      setApplePayAvailable(true)
    },
    [options.getCheckout]
  )

  const submitApplePay = useCallback(async (): Promise<SessionResult> => {
    const sessionId = options.getSessionId()
    const applePayComponent = applePayComponentRef.current as { submit: () => void } | null
    if (!sessionId || !applePayComponent) {
      throw new Error('useApplePay.submitApplePay: component not mounted')
    }

    options.setStatus(AdyenCheckoutStatus.SUBMITTING)

    const attempt = new Promise<never>((_, reject) => {
      rejectApplePayAttemptRef.current = reject
    })

    applePayClaimRef.current = options.claimSession(sessionId)
    applePayClaimRef.current.catch(() => {}) // see specs/app/index.md::Decisions
    applePayComponent.submit()

    const sessionResultPromise = options.waitForSessionResult()

    try {
      const result = await Promise.race([sessionResultPromise, attempt])
      options.setStatus(result.refusal ? AdyenCheckoutStatus.FAILED : AdyenCheckoutStatus.COMPLETED)
      return result
    } catch (error) {
      applePayClaimRef.current = null
      options.setStatus(AdyenCheckoutStatus.READY)
      throw error
    } finally {
      rejectApplePayAttemptRef.current = null
    }
  }, [options.getSessionId, options.claimSession, options.waitForSessionResult, options.setStatus])

  const getPendingClaim = useCallback(() => applePayClaimRef.current, [])

  useEffect(() => {
    return () => {
      const applePayComponent = applePayComponentRef.current as { unmount?: () => void } | null
      try {
        applePayComponent?.unmount?.()
      } catch {}
    }
  }, [])

  return { applePayAvailable, mountApplePay, submitApplePay, getPendingClaim }
}
